import { useCallback, useEffect, useState } from "react";
import { Grid, Button, Skeleton } from "@mui/material";
import { cloneDeep, isEqual } from "lodash-es";

import AddIcon from "@mui/icons-material/Add";

import { calcZones, useSmartalarm } from "./SmartalarmTemplate";
import { IOSettingsValue } from "./types";
import { useDevice } from "../../../../utils/DeviceProvider";
import SmartalarmBoard from "./SmartalarmBoard";
import { useIntl } from "react-intl";
import { uuidv4 } from "../../../../utils/utils";
import TitledCard from "../TitledCard";

const SmartalarmBoards = () => {
  const [boardIds, setBoardIds] = useState<string[] | null>(null);

  const {
    device: { noConfig },
  } = useDevice();
  const { value, saved, setValue } = useSmartalarm();

  const { formatMessage } = useIntl();

  const addNewPanel = useCallback(() => {
    if (!value.panels) {
      value.panels = [];
    }
    value.panels.push({ uuid: uuidv4(), protocol: null, mac: null, ports: [], newItem: true });
    setBoardIds(value.panels.map((p) => p.uuid));
    setValue(value);
  }, [value, setValue]);

  const onBoardValueChange = useCallback(
    (index: number, newValue: IOSettingsValue) => {
      value.panels[index] = cloneDeep(newValue);
      setValue(value);
      calcZones(value.panels).forEach((zone) => {
        if (!value.plists.zonesPlists[zone.uuid]) {
          value.plists.zonesPlists[zone.uuid] = value.plists.partitions.map(() => "0").join("");
        }
      });
    },
    [value, setValue]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setBoardIds((old) => {
      const ids = value.panels.map((p) => p.uuid);
      return isEqual(old, ids) ? old : ids;
    });
  }, [value]);

  if (!value && !noConfig) {
    return (
      <TitledCard>
        <Grid container spacing={1} sx={{ padding: 1 }}>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
        </Grid>
      </TitledCard>
    );
  }

  return (
    <Grid container spacing={1}>
      {value &&
        boardIds?.length === value.panels.length &&
        boardIds.map((_, index) => (
          <Grid item xs={12} key={value.panels[index].uuid}>
            <SmartalarmBoard index={index} value={value.panels[index]} saved={saved?.panels?.[index] || null} onChange={onBoardValueChange} />
          </Grid>
        ))}
      <Grid item xs={12}>
        <Button startIcon={<AddIcon />} sx={{ margin: 1 }} onClick={addNewPanel}>
          {formatMessage({ defaultMessage: "Add new panel" })}
        </Button>
      </Grid>
    </Grid>
  );
};

export default SmartalarmBoards;
